import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, X, AlertTriangle, ExternalLink, Image } from 'lucide-react';
import '../styles/Admin.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const emptyForm = {
  title: '', description: '', features: '', note: '',
  title_en: '', description_en: '', features_en: '', note_en: '',
  link: '', instagram: '', type: 'completed', display_order: '0'
};

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [newImages, setNewImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  // Snimak koji se pusta na hover kartice projekta
  const [existingVideo, setExistingVideo] = useState(null); // putanja sa servera
  const [newVideo, setNewVideo] = useState(null);           // File pre slanja
  const [videoPreview, setVideoPreview] = useState(null);   // objectURL za pregled
  const [langTab, setLangTab] = useState('sr');
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('mstechs_token');
      const res = await fetch(`${API_URL}/api/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setProjects(data.projects);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setExistingVideo(null);
    setNewVideo(null);
    setVideoPreview((u) => { if (u) URL.revokeObjectURL(u); return null; });
    setEditingProject(null);
    setFormError('');
    setNewImages([]);
    setExistingImages([]);
    setPreviewUrls([]);
    setLangTab('sr');
  };

  const openAdd = () => { resetForm(); setShowModal(true); };

  const openEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      features: project.features || '',
      note: project.note || '',
      title_en: project.title_en || '',
      description_en: project.description_en || '',
      features_en: project.features_en || '',
      note_en: project.note_en || '',
      link: project.link || '',
      instagram: project.instagram || '',
      type: project.type,
      display_order: String(project.display_order)
    });
    setExistingImages(project.images || []);
    setExistingVideo(project.video || null);
    setNewVideo(null);
    setVideoPreview(null);
    setNewImages([]);
    setPreviewUrls([]);
    setFormError('');
    setLangTab('sr');
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); resetForm(); };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setFormError('');
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const total = existingImages.length + newImages.length + files.length;
    if (total > 3) {
      setFormError('Maksimalno 3 slike po projektu');
      return;
    }
    const urls = files.map(f => URL.createObjectURL(f));
    setNewImages(prev => [...prev, ...files]);
    setPreviewUrls(prev => [...prev, ...urls]);
    setFormError('');
  };

  const MAX_VIDEO_MB = 40;

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      setFormError(`Snimak prelazi ${MAX_VIDEO_MB} MB. Smanji ga pre otpremanja.`);
      e.target.value = '';
      return;
    }
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setNewVideo(file);
    setVideoPreview(URL.createObjectURL(file));
    setFormError('');
  };

  const clearVideo = () => {
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setNewVideo(null);
    setVideoPreview(null);
    setExistingVideo(null);
  };

  const removeExistingImage = (idx) => {
    setExistingImages(prev => prev.filter((_, i) => i !== idx));
  };

  const removeNewImage = (idx) => {
    URL.revokeObjectURL(previewUrls[idx]);
    setNewImages(prev => prev.filter((_, i) => i !== idx));
    setPreviewUrls(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      setFormError('Naziv i opis (srpski) su obavezni');
      return;
    }
    if (existingImages.length + newImages.length === 0) {
      setFormError('Dodajte najmanje jednu sliku');
      return;
    }
    setSubmitting(true);

    const fd = new FormData();
    Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
    existingImages.forEach(img => fd.append('existingImages', img));
    newImages.forEach(file => fd.append('images', file));
    if (newVideo) fd.append('video', newVideo);
    // Server brise postojeci snimak samo na izricit zahtev
    if (!newVideo && !existingVideo) fd.append('removeVideo', 'true');

    try {
      const token = localStorage.getItem('mstechs_token');
      const url = editingProject
        ? `${API_URL}/api/projects/${editingProject.id}`
        : `${API_URL}/api/projects`;
      const method = editingProject ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      const data = await res.json();
      if (data.success) {
        closeModal();
        fetchProjects();
      } else {
        setFormError(data.message || 'Greška');
      }
    } catch (err) {
      setFormError('Greška pri čuvanju projekta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('mstechs_token');
      const res = await fetch(`${API_URL}/api/projects/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setDeleteConfirm(null);
        fetchProjects();
      }
    } catch (err) { console.error(err); }
  };

  const completedProjects = projects.filter(p => p.type === 'completed');
  const upcomingProjects = projects.filter(p => p.type === 'upcoming');

  if (loading) return (
    <div className="loading-state">
      <div className="loader"></div>
      <p>Učitavanje projekata...</p>
    </div>
  );

  return (
    <div className="clients-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">Projekti</h2>
          <span className="page-subtitle">{projects.length} projekata ukupno</span>
        </div>
        <button className="btn-add" onClick={openAdd}>
          <Plus size={18} />
          Novi projekat
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="empty-state">
          <Image size={48} style={{ color: 'var(--admin-text-muted)', marginBottom: 16 }} />
          <p>Nema projekata. Dodajte prvi projekat.</p>
        </div>
      ) : (
        <>
          {completedProjects.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <h3 style={{ color: 'var(--admin-primary)', marginBottom: 16, fontSize: 14, textTransform: 'uppercase', letterSpacing: 1 }}>
                Realizovani projekti ({completedProjects.length})
              </h3>
              <div className="projects-grid">
                {completedProjects.map(project => (
                  <ProjectCard key={project.id} project={project} onEdit={openEdit} onDelete={setDeleteConfirm} apiUrl={API_URL} />
                ))}
              </div>
            </div>
          )}
          {upcomingProjects.length > 0 && (
            <div>
              <h3 style={{ color: 'var(--admin-warning)', marginBottom: 16, fontSize: 14, textTransform: 'uppercase', letterSpacing: 1 }}>
                Uskoro ({upcomingProjects.length})
              </h3>
              <div className="projects-grid">
                {upcomingProjects.map(project => (
                  <ProjectCard key={project.id} project={project} onEdit={openEdit} onDelete={setDeleteConfirm} apiUrl={API_URL} />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingProject ? 'Izmeni projekat' : 'Novi projekat'}</h3>
              <button className="modal-close" onClick={closeModal}><X size={20} /></button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              {/* Jezički tabovi */}
              <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid var(--admin-border)' }}>
                <button
                  type="button"
                  onClick={() => setLangTab('sr')}
                  style={{
                    padding: '8px 20px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    background: 'transparent',
                    color: langTab === 'sr' ? 'var(--admin-primary)' : 'var(--admin-text-muted)',
                    borderBottom: langTab === 'sr' ? '2px solid var(--admin-primary)' : '2px solid transparent',
                    marginBottom: -1
                  }}
                >
                  🇷🇸 Srpski
                </button>
                <button
                  type="button"
                  onClick={() => setLangTab('en')}
                  style={{
                    padding: '8px 20px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    background: 'transparent',
                    color: langTab === 'en' ? 'var(--admin-primary)' : 'var(--admin-text-muted)',
                    borderBottom: langTab === 'en' ? '2px solid var(--admin-primary)' : '2px solid transparent',
                    marginBottom: -1
                  }}
                >
                  🇬🇧 English
                </button>
              </div>

              {langTab === 'sr' && (
                <div className="form-grid-2">
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Naziv (srpski) *</label>
                    <input name="title" value={formData.title} onChange={handleChange} placeholder="Naziv projekta" />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Opis (srpski) *</label>
                    <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Kratak opis projekta" rows={3} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Funkcionalnosti (srpski)</label>
                    <textarea name="features" value={formData.features} onChange={handleChange} placeholder="Nabrojte funkcionalnosti (opciono)" rows={2} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Napomena (srpski)</label>
                    <input name="note" value={formData.note} onChange={handleChange} placeholder="npr. Demo sajt (opciono)" />
                  </div>
                </div>
              )}

              {langTab === 'en' && (
                <div className="form-grid-2">
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Title (English)</label>
                    <input name="title_en" value={formData.title_en} onChange={handleChange} placeholder="Project title" />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Description (English)</label>
                    <textarea name="description_en" value={formData.description_en} onChange={handleChange} placeholder="Short project description" rows={3} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Features (English)</label>
                    <textarea name="features_en" value={formData.features_en} onChange={handleChange} placeholder="List features (optional)" rows={2} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Note (English)</label>
                    <input name="note_en" value={formData.note_en} onChange={handleChange} placeholder="e.g. Demo site (optional)" />
                  </div>
                </div>
              )}

              {/* Zajednička polja */}
              <div className="form-grid-2" style={{ marginTop: 8 }}>
                <div className="form-group">
                  <label>Link sajta</label>
                  <input name="link" value={formData.link} onChange={handleChange} placeholder="https://..." />
                </div>
                <div className="form-group">
                  <label>Instagram link</label>
                  <input name="instagram" value={formData.instagram} onChange={handleChange} placeholder="https://instagram.com/..." />
                </div>
                <div className="form-group">
                  <label>Tip</label>
                  <select name="type" value={formData.type} onChange={handleChange}>
                    <option value="completed">Realizovan</option>
                    <option value="upcoming">Uskoro</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Redosled prikaza</label>
                  <input type="number" name="display_order" value={formData.display_order} onChange={handleChange} min="0" />
                </div>
              </div>

              {/* Slike */}
              <div className="form-group" style={{ marginTop: 8 }}>
                <label>Slike (max 3)</label>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                  {existingImages.map((img, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <img src={`${API_URL}${img}`} alt="" style={{ width: 100, height: 70, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--admin-border)' }} />
                      <button type="button" onClick={() => removeExistingImage(idx)} style={{ position: 'absolute', top: -6, right: -6, background: 'var(--admin-danger)', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {previewUrls.map((url, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <img src={url} alt="" style={{ width: 100, height: 70, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--admin-primary)', opacity: 0.85 }} />
                      <button type="button" onClick={() => removeNewImage(idx)} style={{ position: 'absolute', top: -6, right: -6, background: 'var(--admin-danger)', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {(existingImages.length + newImages.length) < 3 && (
                    <button type="button" onClick={() => fileInputRef.current?.click()} style={{ width: 100, height: 70, border: '2px dashed var(--admin-border)', borderRadius: 8, background: 'transparent', color: 'var(--admin-text-muted)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, fontSize: 11 }}>
                      <Plus size={18} />
                      Dodaj sliku
                    </button>
                  )}
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFileChange} />
                <span style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>
                  {existingImages.length + newImages.length}/3 slika
                </span>
              </div>

              {/* Snimak sajta — pušta se kad posetilac pređe mišem preko kartice */}
              <div className="form-group" style={{ marginTop: 8 }}>
                <label>Snimak sajta (opciono)</label>

                {(videoPreview || existingVideo) ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
                    <video
                      src={videoPreview || `${API_URL}${existingVideo}`}
                      muted
                      loop
                      playsInline
                      controls
                      style={{ width: 200, height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--admin-border)', background: '#000' }}
                    />
                    <button
                      type="button"
                      onClick={clearVideo}
                      className="btn-icon delete"
                      title="Ukloni snimak"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => videoInputRef.current?.click()}
                    style={{ width: 200, height: 120, border: '2px dashed var(--admin-border)', borderRadius: 8, background: 'transparent', color: 'var(--admin-text-muted)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12 }}
                  >
                    <Plus size={20} />
                    Dodaj snimak
                  </button>
                )}

                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  style={{ display: 'none' }}
                  onChange={handleVideoChange}
                />
                <span style={{ display: 'block', fontSize: 12, color: 'var(--admin-text-muted)', marginTop: 4 }}>
                  Do {MAX_VIDEO_MB} MB. Prikazuje se kad posetilac pređe mišem preko kartice na sajtu.
                  Na telefonu se ne preuzima.
                </span>
              </div>

              {formError && (
                <div className="form-error">
                  <AlertTriangle size={16} />
                  {formError}
                </div>
              )}

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={closeModal}>Otkaži</button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? 'Čuvanje...' : (editingProject ? 'Sačuvaj izmene' : 'Dodaj projekat')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content modal-small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Obriši projekat</h3>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="delete-warning">
                <AlertTriangle size={24} />
                <p>Da li ste sigurni da želite da obrišete <strong>{deleteConfirm.title}</strong>? Slike će biti trajno obrisane.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setDeleteConfirm(null)}>Otkaži</button>
              <button className="btn-delete" onClick={() => handleDelete(deleteConfirm.id)}>Obriši</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProjectCard = ({ project, onEdit, onDelete, apiUrl }) => (
  <div className="project-admin-card">
    <div className="project-admin-images">
      {(project.images || []).slice(0, 3).map((img, idx) => (
        <img key={idx} src={`${apiUrl}${img}`} alt="" />
      ))}
      {(project.images || []).length === 0 && (
        <div className="project-admin-no-image">
          <Image size={24} />
        </div>
      )}
    </div>
    <div className="project-admin-body">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span className={`project-type-badge ${project.type}`}>
          {project.type === 'completed' ? 'Realizovan' : 'Uskoro'}
        </span>
        <span style={{ fontSize: 11, color: 'var(--admin-text-muted)' }}>#{project.display_order}</span>
        {project.title_en && (
          <span style={{ fontSize: 10, color: 'var(--admin-text-muted)', background: 'var(--admin-border)', borderRadius: 4, padding: '1px 6px' }}>EN</span>
        )}
      </div>
      <h4 className="project-admin-title">{project.title}</h4>
      <p className="project-admin-desc">{project.description}</p>
      {project.link && (
        <a href={project.link} target="_blank" rel="noopener noreferrer" className="project-admin-link">
          <ExternalLink size={12} /> {project.link}
        </a>
      )}
    </div>
    <div className="project-admin-actions">
      <button className="btn-icon btn-edit" onClick={() => onEdit(project)} title="Izmeni">
        <Edit2 size={16} />
      </button>
      <button className="btn-icon btn-danger" onClick={() => onDelete(project)} title="Obriši">
        <Trash2 size={16} />
      </button>
    </div>
  </div>
);

export default Projects;
